// Pure tier-matrix membership logic — no React, no I/O. This is the heart of
// the role editor: it derives what scopes/permissions a matrix selection
// resolves to, and the reverse (what matrix state a role's saved membership
// implies), following the same tier/wildcard rules the server's
// CatalogProjection.expandGrants uses, but scoped down to one domain at a
// time so the matrix can show independent per-domain state.
import type { DomainState, ProductStructure, RoleMembership, TierId } from '../types'

function findDomain(structure: ProductStructure, domain: string) {
  return structure.domains.find((candidate) => candidate.domain === domain)
}

function findTier(structure: ProductStructure, tier: TierId) {
  return structure.tiers.find((candidate) => candidate.id === tier)
}

function sortedUnique(values: Iterable<string>): string[] {
  return [...new Set(values)].sort()
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((value, index) => value === sortedB[index])
}

// A verb is read-class iff its permission id appears in the viewer tier's
// expanded permission set — the server-side rule (CatalogProjection.isReadVerb)
// derives this from get/list vs create/update/delete/undelete, with a custom
// verb (sync, open, …) falling to read-class only when the resource has no
// write scope at all. We don't reimplement that name heuristic on the
// frontend; the viewer tier's own permission list already reflects it.
export function isReadClassVerb(structure: ProductStructure, domain: string, resourceId: string, verb: string): boolean {
  const viewer = findTier(structure, 'viewer')
  if (!viewer) return false
  return viewer.permissions.includes(`${domain}.${resourceId}.${verb}`)
}

// The verbs a whole-tier selection would turn on for each resource in a
// domain — viewer gets only the read-class verbs, editor/admin get all of
// them (domainAll on the server).
export function tierVerbsByResource(structure: ProductStructure, domain: string, tier: TierId): Record<string, string[]> {
  const domainDef = findDomain(structure, domain)
  if (!domainDef) return {}
  const out: Record<string, string[]> = {}
  for (const resource of domainDef.resources) {
    out[resource.id] =
      tier === 'viewer' ? resource.verbs.filter((verb) => isReadClassVerb(structure, domain, resource.id, verb)) : [...resource.verbs]
  }
  return out
}

// The membership a whole-tier selection resolves to for one domain: the
// tier's product-wide permission set filtered to this domain's prefix, plus
// the domain's resources' read scopes (viewer) or read+write scopes
// (editor/admin) — derived straight from the domain's own resource defs
// rather than by re-filtering the tier's scope list, since a scope's wire
// value carries no domain prefix to filter on.
export function domainTierMembership(structure: ProductStructure, domain: string, tier: TierId): RoleMembership {
  const domainDef = findDomain(structure, domain)
  const tierDef = findTier(structure, tier)
  if (!domainDef || !tierDef) return { scopes: [], permissions: [] }

  const prefix = `${domain}.`
  const permissions = tierDef.permissions.filter((permission) => permission.startsWith(prefix))

  const scopes = new Set<string>()
  for (const resource of domainDef.resources) {
    if (resource.readScope) scopes.add(resource.readScope.value)
    if (tier !== 'viewer' && resource.writeScope) scopes.add(resource.writeScope.value)
  }

  return { scopes: sortedUnique(scopes), permissions: sortedUnique(permissions) }
}

// The membership an explicit per-resource verb selection resolves to for one
// domain (the "custom"/advanced state): a resource's read scope is included
// when any of its read-class verbs is selected, its write scope when any of
// its write-class verbs is.
export function domainCustomMembership(
  structure: ProductStructure,
  domain: string,
  verbsByResource: Record<string, string[]>,
): RoleMembership {
  const domainDef = findDomain(structure, domain)
  if (!domainDef) return { scopes: [], permissions: [] }

  const scopes = new Set<string>()
  const permissions = new Set<string>()
  for (const resource of domainDef.resources) {
    const selected = verbsByResource[resource.id] ?? []
    for (const verb of selected) {
      if (!resource.verbs.includes(verb)) continue
      permissions.add(`${domain}.${resource.id}.${verb}`)
      if (isReadClassVerb(structure, domain, resource.id, verb)) {
        if (resource.readScope) scopes.add(resource.readScope.value)
      } else if (resource.writeScope) {
        scopes.add(resource.writeScope.value)
      }
    }
  }

  return { scopes: sortedUnique(scopes), permissions: sortedUnique(permissions) }
}

// The membership every domain row's *current* state re-emits, VALUE-keyed —
// the same shape a saved role's membership carries on read (see the
// asymmetry note above buildMembership). Shared by buildMembership (before
// it re-keys scopes to ids) and residueMembership (to detect what a saved
// role carries that no domain's current state re-emits).
function reemittedMembership(structure: ProductStructure, states: Record<string, DomainState>): RoleMembership {
  const scopes = new Set<string>()
  const permissions = new Set<string>()
  for (const domainDef of structure.domains) {
    const state = states[domainDef.domain] ?? { kind: 'none' }
    const membership = domainMembership(structure, domainDef.domain, state)
    membership.scopes.forEach((value) => scopes.add(value))
    membership.permissions.forEach((permission) => permissions.add(permission))
  }
  return { scopes: sortedUnique(scopes), permissions: sortedUnique(permissions) }
}

// A residue scope's fate once it's re-keyed value → id for the wire (see the
// asymmetry note below): translatable when the structure still declares the
// resource it belongs to (`preserved`, safe to resubmit as an id), or
// untranslatable when it doesn't (`dropped`) — a raw untranslatable value
// can never be resubmitted, since the server validates ids on write and
// would 400 the whole save (PgCustomRoles.assertGrantsExist).
export interface ScopeResiduePartition {
  preserved: string[]
  dropped: string[]
}

// Residue permissions have no such split — a permission's id IS its value
// (see the asymmetry note below) — so they always round-trip as-is.
export interface ResidueMembership {
  scopes: ScopeResiduePartition
  permissions: string[]
}

function partitionScopeResidue(structure: ProductStructure, values: string[]): ScopeResiduePartition {
  const preserved: string[] = []
  const dropped: string[] = []
  for (const value of values) {
    const id = scopeIdFor(structure, value)
    if (id) {
      preserved.push(id)
    } else {
      dropped.push(value)
    }
  }
  return { preserved: sortedUnique(preserved), dropped: sortedUnique(dropped) }
}

// Grants the matrix can no longer express: whatever a saved role's
// membership contains that the domain rows' *current* derived state doesn't
// re-emit for any domain. This happens when the yaml catalog drops a
// resource/verb out from under an existing role (its saved scope/permission
// no longer matches any resource in the structure) or a scope ends up gated
// by no permission at all in its own domain. Without this, buildMembership
// would silently drop such a grant the next time the role is saved — see
// its `saved` param, which unions the translatable (preserved) share back in
// unchanged. The untranslatable (dropped) share can't be carried forward at
// all — see ScopeResiduePartition — so callers must surface it rather than
// resubmit it.
export function residueMembership(structure: ProductStructure, states: Record<string, DomainState>, saved: RoleMembership): ResidueMembership {
  const reemitted = reemittedMembership(structure, states)
  const reemittedScopes = new Set(reemitted.scopes)
  const reemittedPermissions = new Set(reemitted.permissions)
  const residueScopeValues = sortedUnique(saved.scopes.filter((value) => !reemittedScopes.has(value)))
  const residuePermissions = sortedUnique(saved.permissions.filter((permission) => !reemittedPermissions.has(permission)))
  return {
    scopes: partitionScopeResidue(structure, residueScopeValues),
    permissions: residuePermissions,
  }
}

// ── Scope representation asymmetry (server quirk) ──────────────────────
// Writing a role (PgCustomRoles.create/update) validates + stores delegation
// SCOPE IDS — idp_role_scopes.scope_id references idp_scopes.id. Reading a
// role back (PgRoleCatalog.roles, GET /v1/admin/catalog/roles) resolves that
// same grant to its wire VALUE instead (idp_scopes.value — the legacy alias
// live JWTs still carry), for wire compatibility. So a role's saved
// membership is VALUE-keyed coming in (deriveDomainState/deriveStates, the
// tier candidates they compare against, and residueMembership above all
// work in that space, matching what a GET response actually contains), but
// buildMembership must re-key scopes to their id before they go back out on
// the wire, or every scope-bearing write 400s (UnknownGrantException).
// Permissions have no such split — a permission's id IS its value
// (CatalogProjection.Permission) — so they pass through unchanged either way.
//
// The read side also INNER-joins against idp_scopes, so a stored id that no
// longer resolves there is silently dropped before the client ever sees it —
// a role's GET response can never contain a truly orphaned scope. That means
// residueMembership's input is already scrubbed of that case; the residue it
// finds is always a scope still live somewhere in idp_scopes, just not one
// the *current structure* (a possibly-narrower or since-changed catalog
// view) can still resolve — which is exactly the untranslatable/"dropped"
// case ScopeResiduePartition reports.
function scopeIdFor(structure: ProductStructure, value: string): string | undefined {
  for (const domainDef of structure.domains) {
    for (const resource of domainDef.resources) {
      if (resource.readScope?.value === value) return resource.readScope.id
      if (resource.writeScope?.value === value) return resource.writeScope.id
    }
  }
  return undefined
}

// Toggle one verb on/off for a domain row, converting a whole-tier selection
// into its equivalent per-verb form first so the toggle is additive/subtractive
// from what was actually granted, not from a blank slate.
export function toggleVerb(structure: ProductStructure, domain: string, state: DomainState, resourceId: string, verb: string): DomainState {
  const current: Record<string, string[]> =
    state.kind === 'custom' ? state.verbsByResource : state.kind === 'tier' ? tierVerbsByResource(structure, domain, state.tier) : {}

  const selected = new Set(current[resourceId] ?? [])
  if (selected.has(verb)) {
    selected.delete(verb)
  } else {
    selected.add(verb)
  }

  return { kind: 'custom', verbsByResource: { ...current, [resourceId]: [...selected] } }
}

// The membership one domain row's state resolves to, regardless of shape.
export function domainMembership(structure: ProductStructure, domain: string, state: DomainState): RoleMembership {
  switch (state.kind) {
    case 'tier':
      return domainTierMembership(structure, domain, state.tier)
    case 'custom':
      return domainCustomMembership(structure, domain, state.verbsByResource)
    case 'none':
    default:
      return { scopes: [], permissions: [] }
  }
}

// Aggregate every domain row's state into the role's full membership — what
// actually gets submitted. Scopes are re-keyed value → id on the way out
// (see the asymmetry note above). Pass the role's `saved` membership (as
// read, i.e. value-keyed) to union in any residue it carries — grants the
// current matrix can no longer express — unchanged, instead of dropping it.
export function buildMembership(structure: ProductStructure, states: Record<string, DomainState>, saved?: RoleMembership): RoleMembership {
  const reemitted = reemittedMembership(structure, states)

  const scopeIds = new Set<string>()
  reemitted.scopes.forEach((value) => {
    const id = scopeIdFor(structure, value)
    if (id) scopeIds.add(id)
  })

  const permissions = new Set<string>(reemitted.permissions)

  if (saved) {
    const residue = residueMembership(structure, states, saved)
    residue.scopes.preserved.forEach((id) => scopeIds.add(id))
    residue.permissions.forEach((permission) => permissions.add(permission))
  }

  return { scopes: sortedUnique(scopeIds), permissions: sortedUnique(permissions) }
}

// Round-trip: given a role's explicit membership (its saved scopes/permissions,
// or a preset's), derive what one domain row's matrix state must have been —
// an exact tier match wins, a partial grant becomes "custom" with the
// expander reflecting the selected verbs, and no grant at all is "none".
//
// Editor and admin currently expand identically for a domain (no admin-only
// ops yet — see Tier docs), so a saved grant that matches both is genuinely
// ambiguous; ties resolve to whichever tier is listed first in the server's
// `tiers` array (viewer, editor, admin), i.e. "editor" wins over "admin".
export function deriveDomainState(structure: ProductStructure, domain: string, membership: RoleMembership): DomainState {
  const domainDef = findDomain(structure, domain)
  if (!domainDef) return { kind: 'none' }

  const prefix = `${domain}.`
  const domainPermissions = membership.permissions.filter((permission) => permission.startsWith(prefix))
  const domainScopes = membership.scopes.filter((scope) =>
    domainDef.resources.some((resource) => resource.readScope?.value === scope || resource.writeScope?.value === scope),
  )

  if (domainPermissions.length === 0 && domainScopes.length === 0) {
    return { kind: 'none' }
  }

  for (const tier of structure.tiers) {
    const candidate = domainTierMembership(structure, domain, tier.id)
    if (sameSet(candidate.permissions, domainPermissions) && sameSet(candidate.scopes, domainScopes)) {
      return { kind: 'tier', tier: tier.id }
    }
  }

  const verbsByResource: Record<string, string[]> = {}
  for (const resource of domainDef.resources) {
    const verbs = resource.verbs.filter((verb) => domainPermissions.includes(`${domain}.${resource.id}.${verb}`))
    if (verbs.length > 0) verbsByResource[resource.id] = verbs
  }
  return { kind: 'custom', verbsByResource }
}

// Derive every domain row's state from a role's (or a preset's) full
// membership — used both for the edit-mode round trip and to seed the editor
// from a clone-from-preset pick.
export function deriveStates(structure: ProductStructure, membership: RoleMembership): Record<string, DomainState> {
  const states: Record<string, DomainState> = {}
  for (const domainDef of structure.domains) {
    states[domainDef.domain] = deriveDomainState(structure, domainDef.domain, membership)
  }
  return states
}
